import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  handleCreateSysIdentification,
  handleEditSysIdentification,
  handleLocationsSystemIdentification,
  handleRemoveSysIdentificationLocation,
} from '../../store/slices/settings/admin/sysIdentificationSlice';
import { selectDescription } from '../../store/slices/ssrDescriptionSlice';
import {
  createUOSZoneService,
  getZonesInfoForSystemIdentificationService,
  updateUOSZoneService,
} from '../../../services';
import {
  describeZonesError,
  runSystemIdentificationPersistence,
  sysIdModeToFlag,
} from '../../../helpers/setting';
import { logger } from '../../../helpers/logger';
import {
  systemIdentificationCreatePayload,
  systemIdentificationUpdatePayload,
} from '../../../helpers/payloads/systemIdentificationPayload';
import { plainMessage } from './settingsMessage';

const CREATED =
  'new location and switches of system identification settings have been created';
const MODIFIED =
  'location and/or switches info of system identification settings have been modified';
const DELETED =
  'selected location of system identification settings has been deleted';
const NO_ZONE_ID =
  'this location carries no zone id, so it cannot be deleted from here. reload the page and try again.';

const blankSwitchRow = () => ({
  UOS: '',
  switchName: '',
  heatingSys: '',
  gasType: '',
  selectedSSR: [],
  application: '',
  switchSize: '',
  ssrRating: '',
  sysId: '',
  displaySelectBox: [false, false, false, false, false, false],
  deviceName: null,
  zoneId: null,
  deviceMac: null,
  type: null,
  latitude: null,
  longitude: null,
});

const outcome = (title, message, failed = false) => ({
  title,
  message,
  failed,
});

// The confirmed form holds one array of rows per panel; each group must be an
// array (a non-array group throws, as the page always did).
const flattenGroups = (groups) => {
  const rows = [];
  groups.forEach((group) => {
    group.forEach((row) => {
      rows.push(row);
    });
  });
  return rows;
};

/**
 * The system-identification form of the admin panel (add, edit or delete a
 * zone and its panels). `save(change)` sends what the form holds and
 * resolves to `{ title, message, failed }`; `failed` keeps the form open.
 * Saves that aren't awaited report a failure through `showMessage`.
 * `openHeaders` must be a new array each time a header opens or closes: the
 * address fields clear on its identity.
 */
export const useSaveSystemIdentification = ({
  openHeaders,
  refetchAllSystems,
  showMessage,
}) => {
  const dispatch = useDispatch();
  const { elementsOptions } = useSelector(selectDescription);

  const [sysIdMode, setSysIdMode] = useState(null);
  const isCreateEditOrSave = sysIdModeToFlag(sysIdMode);
  const [numOfUOS, setNumOfUOS] = useState('');
  const [numOfSSR, setNumOfSSR] = useState('');
  const [numSwitches, setNumSwitches] = useState('');
  const [UOSName, setUOSName] = useState('');
  const [UOSDelete, setUOSDelete] = useState([]);
  const [sysIdResyncToken, setSysIdResyncToken] = useState(0);
  const locationNameRef = useRef();
  const [civicAddress, setCivicAddress] = useState('');
  const [specificLocation, setSpecificLocation] = useState('');
  const [switches, setSwitches] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isGroupedSwitchesSaved, setIsGroupedSwitchesSaved] = useState([]);
  const [isConfirmed, setIsConfirmed] = useState(false);

  useEffect(() => {
    setCivicAddress('');
    setSpecificLocation('');
  }, [openHeaders]);

  const form = (locationName) => ({
    locationName,
    specificLocation,
    civicAddress,
    numOfUOS,
    numOfSSR,
    numSwitches,
    UOSName,
    UOSDelete,
  });

  const refreshLocations = () =>
    getZonesInfoForSystemIdentificationService()
      .then((res) => {
        dispatch(
          handleLocationsSystemIdentification({
            data: res,
            heaterSpecs: elementsOptions,
          }),
        );
        setSysIdResyncToken((token) => token + 1);
      })
      .catch((error) => {
        logger.error(error);
        showMessage(
          plainMessage(
            'saved, but not refreshed',
            `the change was saved. ${describeZonesError(
              error,
            )} reload the page to see the current list.`,
          ),
        );
      });

  const reportSaveFailure = (error) => {
    logger.error(error);
    showMessage(plainMessage('save failed', describeZonesError(error)));
  };

  const failedWith = (error) => {
    logger.error(error);
    return outcome('save failed', describeZonesError(error), true);
  };

  const persist = (request, onSuccess) =>
    runSystemIdentificationPersistence({
      request,
      onSuccess,
      refresh: refreshLocations,
    });

  const exitToChooser = () => {
    setSysIdMode(null);
    setIsConfirmed(false);
    setSelectedLocation(null);
    setCivicAddress('');
    setSpecificLocation('');
    setNumOfUOS('');
    setNumOfSSR('');
    setNumSwitches('');
    setSwitches([]);
    setIsGroupedSwitchesSaved([]);
    setUOSName([]);
  };

  const recordCreated = (locationName, switchInfo) =>
    dispatch(
      handleCreateSysIdentification({
        specificLocationName: specificLocation,
        address: civicAddress,
        location: locationName,
        specificLocationInfo: {
          numOfUOS,
          numOfSSR,
          switchesNum: switchInfo.length,
          switchInfo,
        },
      }),
    );

  const recordEdited = (locationName, switchInfo, switchesNum) =>
    dispatch(
      handleEditSysIdentification({
        locationIdx: selectedLocation.locationIdx,
        modifiedLocation: {
          location: locationName,
          specificLocation,
          address: civicAddress,
          numOfUOS,
          numOfSSR,
          switchesNum,
          switchInfo,
        },
        originalSpecificLocation: selectedLocation?.specificLocation,
        isEditAll: false,
      }),
    );

  const createInBackground = (switchRows, locationName) => {
    createUOSZoneService(
      systemIdentificationCreatePayload(switchRows, form(locationName)),
    )
      .then(() => {
        refetchAllSystems();
        recordCreated(locationName, switchRows);
      })
      .then(() => {
        refreshLocations();
      })
      .catch(reportSaveFailure);
  };

  const saveNewLocation = async (change, locationName) => {
    if (change === 'someUOSSaved') {
      createInBackground(
        isGroupedSwitchesSaved.flatMap((saved, idx) =>
          saved ? [switches[idx]] : [],
        ),
        locationName,
      );
    } else if (change === 'allTheUOSSaved' && isConfirmed) {
      const switchRows = flattenGroups(switches);
      const failure = await persist(
        () =>
          createUOSZoneService(
            systemIdentificationCreatePayload(switchRows, form(locationName)),
          ),
        () => {
          refetchAllSystems();
          recordCreated(locationName, switchRows);
        },
      );
      if (failure) {
        return failedWith(failure);
      }
    } else {
      // Any other change creates the whole form (edit mode returns null for
      // it), as the page always did.
      createInBackground(switches, locationName);
    }
    return outcome('change options', CREATED);
  };

  const deleteLocation = async (locationName) => {
    const deletePayload = systemIdentificationUpdatePayload(
      switches,
      form(locationName),
    );
    if (!deletePayload.zone_id) {
      return outcome('cannot delete', NO_ZONE_ID, true);
    }
    const failure = await persist(
      () => updateUOSZoneService(deletePayload),
      () => {
        refetchAllSystems();
        dispatch(
          handleRemoveSysIdentificationLocation({
            locationIdx: selectedLocation.locationIdx,
          }),
        );
        setUOSDelete([]);
        exitToChooser();
      },
    );
    return failure ? failedWith(failure) : outcome('change options', DELETED);
  };

  const saveExistingLocation = async (change, locationName) => {
    if (change === 'deleteLocation') {
      return deleteLocation(locationName);
    }

    if (change === 'someUOSSaved') {
      isGroupedSwitchesSaved.forEach((saved, idx) => {
        if (saved) {
          recordEdited(locationName, switches[idx], switches.length);
        }
      });
      updateUOSZoneService(
        systemIdentificationUpdatePayload(switches, form(locationName)),
      )
        .then(() => {
          refetchAllSystems();
          setUOSDelete([]);
          refreshLocations();
        })
        .catch(reportSaveFailure);
      return outcome('change options', MODIFIED);
    }

    if (change !== 'allTheUOSSaved') {
      return null;
    }

    if (isConfirmed) {
      const switchRows = flattenGroups(switches);
      const failure = await persist(
        () =>
          updateUOSZoneService(
            systemIdentificationUpdatePayload(switchRows, form(locationName)),
          ),
        () => {
          refetchAllSystems();
          recordEdited(locationName, switchRows, switchRows.length);
          setUOSDelete([]);
        },
      );
      if (failure) {
        return failedWith(failure);
      }
      return outcome('change options', MODIFIED);
    }

    updateUOSZoneService(
      systemIdentificationUpdatePayload(switches, form(locationName)),
    )
      .then(() => {
        refetchAllSystems();
        dispatch(
          handleEditSysIdentification({
            locationIdx: selectedLocation.locationIdx,
            specificLocationName: specificLocation,
            modifiedLocation: {
              location: locationName,
              address: civicAddress,
              specificLocation: {
                [specificLocation ?? 'info']: {
                  numOfUOS,
                  numOfSSR,
                  switchesNum: switches.length,
                  switchInfo: switches,
                },
              },
            },
            isEditAll: true,
          }),
        );
        setUOSDelete([]);
        refreshLocations();
      })
      .catch(reportSaveFailure);
    return outcome('change options', MODIFIED);
  };

  const save = async (change) => {
    const locationName =
      locationNameRef.current?.value ?? selectedLocation?.location ?? '';
    if (isCreateEditOrSave === 0) {
      return saveNewLocation(change, locationName);
    }
    if (isCreateEditOrSave === 1) {
      return saveExistingLocation(change, locationName);
    }
    return null;
  };

  const cancel = () => {
    setCivicAddress('');
    setSpecificLocation('');
    setNumSwitches('');
    setNumOfUOS('');
    setNumOfSSR('');
    setSwitches([blankSwitchRow()]);
    setSysIdMode(null);
    setIsConfirmed(false);
  };

  return {
    panelProps: {
      isCreateEditOrSave,
      sysIdMode,
      setSysIdMode,
      locationNameRef,
      civicAddress,
      setCivicAddress,
      specificLocation,
      setSpecificLocation,
      switches,
      setSwitches,
      selectedLocation,
      setSelectedLocation,
      numSwitches,
      setNumSwitches,
      numOfUOS,
      setNumOfUOS,
      numOfSSR,
      setNumOfSSR,
      UOSName,
      setUOSName,
      UOSDelete,
      setUOSDelete,
      sysIdResyncToken,
      isGroupedSwitchesSaved,
      setIsGroupedSwitchesSaved,
      isConfirmed,
      setIsConfirmed,
    },
    save,
    cancel,
  };
};
